import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, RefreshCw, Save } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { api } from '../../services/api';
import type { ReportingConfig, ReportingReadinessResponse, ReportingReferenceData } from '../../services/types';

const DEFAULT_CONFIG: ReportingConfig = {
    account_reporting_roles: {},
    account_cost_behaviors: {},
    account_pnl_groups: {},
    account_bs_groups: {},
    wip_policy: {
        formula_key: 'actual_accumulated_cost',
        approval_flag: false,
        effective_date: '',
    },
    product_families: [],
    item_family_mappings: {},
    grc_component_buckets: {},
    report_release_approvals: {
        'cash-flow': false,
        'wip-report': false,
        'break-even': false,
        'product-profitability': false,
        'grc-material-consumption': false,
    },
};

const readinessLabel: Record<string, string> = {
    ready_for_release: 'Ready for release',
    pending_approval: 'Pending approval',
    setup_incomplete: 'Setup incomplete',
};

const readinessClass: Record<string, string> = {
    ready_for_release: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    pending_approval: 'border-amber-200 bg-amber-50 text-amber-700',
    setup_incomplete: 'border-rose-200 bg-rose-50 text-rose-700',
};

function normalizeConfig(config?: Partial<ReportingConfig> | null): ReportingConfig {
    return {
        ...DEFAULT_CONFIG,
        ...config,
        wip_policy: {
            ...DEFAULT_CONFIG.wip_policy,
            ...(config?.wip_policy || {}),
        },
        account_reporting_roles: { ...(config?.account_reporting_roles || {}) },
        account_cost_behaviors: { ...(config?.account_cost_behaviors || {}) },
        account_pnl_groups: { ...(config?.account_pnl_groups || {}) },
        account_bs_groups: { ...(config?.account_bs_groups || {}) },
        product_families: [...(config?.product_families || [])],
        item_family_mappings: { ...(config?.item_family_mappings || {}) },
        grc_component_buckets: { ...(config?.grc_component_buckets || {}) },
        report_release_approvals: {
            ...DEFAULT_CONFIG.report_release_approvals,
            ...(config?.report_release_approvals || {}),
        },
    };
}

export function ReportingSettingsTab() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState<ReportingConfig>(DEFAULT_CONFIG);
    const [referenceData, setReferenceData] = useState<ReportingReferenceData | null>(null);
    const [readiness, setReadiness] = useState<ReportingReadinessResponse | null>(null);
    const [newFamily, setNewFamily] = useState('');

    const loadAll = async () => {
        try {
            setLoading(true);
            const [configPayload, referencePayload, readinessPayload] = await Promise.all([
                api.settings.getReportingConfig(),
                api.settings.getReportingReferenceData(),
                api.settings.getReportingReadiness(),
            ]);
            setConfig(normalizeConfig(configPayload));
            setReferenceData(referencePayload);
            setReadiness(readinessPayload);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load reporting setup');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadAll();
    }, []);

    const families = useMemo(() => {
        const all = new Set((config.product_families || []).map((entry) => entry.trim()).filter(Boolean));
        Object.values(config.item_family_mappings || {}).forEach((entry) => {
            const value = String(entry || '').trim();
            if (value) all.add(value);
        });
        return Array.from(all).sort((a, b) => a.localeCompare(b));
    }, [config.item_family_mappings, config.product_families]);

    const handleSave = async () => {
        try {
            setSaving(true);
            const payload = normalizeConfig({ ...config, product_families: families });
            const saved = await api.settings.updateReportingConfig(payload);
            setConfig(normalizeConfig(saved));
            const [nextReference, nextReadiness] = await Promise.all([
                api.settings.getReportingReferenceData(),
                api.settings.getReportingReadiness(),
            ]);
            setReferenceData(nextReference);
            setReadiness(nextReadiness);
            toast.success('Reporting setup saved');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to save reporting setup');
        } finally {
            setSaving(false);
        }
    };

    const upsertFamily = () => {
        const token = newFamily.trim();
        if (!token) return;
        if (!families.includes(token)) {
            setConfig((current) => ({
                ...current,
                product_families: [...families, token],
            }));
        }
        setNewFamily('');
    };

    if (loading) {
        return <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading reporting setup...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-xl font-semibold text-slate-900">Reporting Setup</h2>
                    <p className="mt-1 text-sm text-slate-500">Maintain policy, product-family, GRC, and release-readiness setup. Statement account grouping now lives in Financials.</p>
                </div>
                <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => void loadAll()} disabled={saving}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Refresh
                    </Button>
                    <Button type="button" onClick={() => void handleSave()} disabled={saving}>
                        <Save className="mr-2 h-4 w-4" />
                        Save Setup
                    </Button>
                </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {Object.entries(readiness?.reports || {}).map(([reportId, state]) => (
                    <Card key={reportId}>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base capitalize">{reportId.replace(/-/g, ' ')}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${readinessClass[state.status]}`}>
                                {readinessLabel[state.status] || state.status}
                            </span>
                            <div className="text-sm text-slate-600">{state.blocker}</div>
                            <div className="text-xs text-slate-500">Missing count: {state.missing_count}</div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Statement Grouping Ownership</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                        Profit &amp; Loss and Balance Sheet account classification is now maintained from the Chart of Accounts so finance users can manage hierarchy and statement grouping in one place.
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 p-4">
                        <div>
                            <div className="font-medium text-slate-900">Open Financials &gt; Chart of Accounts</div>
                            <div className="mt-1 text-sm text-slate-500">Use the COA workspace for active status, parent structure, and statement grouping changes.</div>
                        </div>
                        <Button asChild type="button" variant="outline">
                            <Link to="/financials?tab=coa">Open Chart of Accounts</Link>
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>WIP Policy</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-3">
                    <label className="space-y-2 text-sm">
                        <span className="font-medium text-slate-700">Formula</span>
                        <select
                            className="w-full rounded-md border border-slate-300 px-3 py-2"
                            value={config.wip_policy.formula_key || 'actual_accumulated_cost'}
                            onChange={(event) =>
                                setConfig((current) => ({
                                    ...current,
                                    wip_policy: {
                                        ...current.wip_policy,
                                        formula_key: event.target.value,
                                    },
                                }))
                            }
                        >
                            <option value="actual_accumulated_cost">Actual Accumulated Cost</option>
                        </select>
                    </label>
                    <label className="space-y-2 text-sm">
                        <span className="font-medium text-slate-700">Effective Date</span>
                        <Input
                            type="date"
                            value={config.wip_policy.effective_date || ''}
                            onChange={(event) =>
                                setConfig((current) => ({
                                    ...current,
                                    wip_policy: {
                                        ...current.wip_policy,
                                        effective_date: event.target.value,
                                    },
                                }))
                            }
                        />
                    </label>
                    <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm">
                        <input
                            type="checkbox"
                            checked={Boolean(config.wip_policy.approval_flag)}
                            onChange={(event) =>
                                setConfig((current) => ({
                                    ...current,
                                    wip_policy: {
                                        ...current.wip_policy,
                                        approval_flag: event.target.checked,
                                    },
                                }))
                            }
                        />
                        <span>WIP valuation policy approved</span>
                    </label>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Product Families</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                        {families.map((family) => (
                            <span key={family} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                {family}
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Input value={newFamily} onChange={(event) => setNewFamily(event.target.value)} placeholder="Add product family..." />
                        <Button type="button" variant="outline" onClick={upsertFamily}>Add</Button>
                    </div>
                    <div className="space-y-2">
                        {(referenceData?.finished_goods || []).map((item) => (
                            <div key={item.item_id} className="grid gap-2 rounded-xl border border-slate-200 p-3 md:grid-cols-[minmax(0,1fr),220px]">
                                <div className="min-w-0">
                                    <div className="font-medium text-slate-900">{item.code} · {item.name}</div>
                                    <div className="text-xs text-slate-500">{item.category || 'Finished good'}</div>
                                </div>
                                <select
                                    className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                                    value={config.item_family_mappings[String(item.item_id)] || ''}
                                    onChange={(event) =>
                                        setConfig((current) => ({
                                            ...current,
                                            item_family_mappings: {
                                                ...current.item_family_mappings,
                                                [String(item.item_id)]: event.target.value,
                                            },
                                        }))
                                    }
                                >
                                    <option value="">Not mapped</option>
                                    {families.map((family) => (
                                        <option key={family} value={family}>{family}</option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>GRC Component Buckets</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                    {(referenceData?.grc_materials || []).map((material) => (
                        <div key={material.material_id} className="grid gap-2 rounded-xl border border-slate-200 p-3 md:grid-cols-[minmax(0,1fr),220px]">
                            <div className="min-w-0">
                                <div className="font-medium text-slate-900">{material.item_code || `MAT-${material.material_id}`} · {material.item_name || material.material_name}</div>
                                <div className="text-xs text-slate-500">{material.material_name}</div>
                            </div>
                            <select
                                className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                                value={config.grc_component_buckets[String(material.item_id)] || ''}
                                onChange={(event) =>
                                    setConfig((current) => ({
                                        ...current,
                                        grc_component_buckets: {
                                            ...current.grc_component_buckets,
                                            [String(material.item_id)]: event.target.value,
                                        },
                                    }))
                                }
                                disabled={!material.item_id}
                            >
                                <option value="">Not mapped</option>
                                <option value="cement">Cement</option>
                                <option value="sand">Sand</option>
                                <option value="fiber">Fiber</option>
                                <option value="additive">Additive</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                    ))}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Release Approvals</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {Object.entries(config.report_release_approvals || {}).map(([reportId, approved]) => (
                        <label key={reportId} className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm">
                            <input
                                type="checkbox"
                                checked={Boolean(approved)}
                                onChange={(event) =>
                                    setConfig((current) => ({
                                        ...current,
                                        report_release_approvals: {
                                            ...current.report_release_approvals,
                                            [reportId]: event.target.checked,
                                        },
                                    }))
                                }
                            />
                            <span className="capitalize">{reportId.replace(/-/g, ' ')}</span>
                        </label>
                    ))}
                </CardContent>
            </Card>
        </div>
    );
}
